import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as S from "./Signup.styled";
import { universityService, University, Department } from 'services/universityService';
import { authService } from 'services/authService';
import { memberService } from 'services/memberService';
import defaultProfileImg from 'assets/MemberImage/defaultProfileImage.png';
import viewIcon from 'assets/SignupImage/view.png';

function Signup() {
  const [step, setStep] = useState(1);
  const [universityId, setUniversityId] = useState<number>(0);
  const [, setUniversityName] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [universities, setUniversities] = useState<University[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [isRequestingCode, setIsRequestingCode] = useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [verificationToken, setVerificationToken] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [studentId, setStudentId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<number>(0);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filteredDepartments, setFilteredDepartments] = useState<Department[]>([]);
  const [departmentSearchText, setDepartmentSearchText] = useState<string>('');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [hasReachedStep3, setHasReachedStep3] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const data = await universityService.getUniversities();
        setUniversities(data);
      } catch (error) {
        console.error('대학교 목록 조회 실패:', error);
      }
    };

    fetchUniversities();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setShowDepartmentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (step === 2 && universityId && departments.length === 0) {
      const fetchDepartments = async () => {
        try {
          const data = await universityService.getDepartments(universityId);
          setDepartments(data);
          setFilteredDepartments(data);
        } catch (error) {
          console.error('학과 목록 조회 실패:', error);
        }
      };
      fetchDepartments();
    }
  }, [step, universityId, departments.length]);

  useEffect(() => {
    if (remainingTime > 0) {
      timerRef.current = setTimeout(() => {
        setRemainingTime(remainingTime - 1);
      }, 1000);
    } else if (remainingTime === 0 && isCodeSent) {
      setIsCodeSent(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [remainingTime, isCodeSent]);

  useEffect(() => {
    if (searchText) {
      const filtered = universities.filter(uni =>
        uni.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredUniversities(filtered);
    } else {
      setFilteredUniversities(universities);
    }
  }, [searchText, universities]);

  useEffect(() => {
    if (departmentSearchText) {
      const filtered = departments.filter(dept =>
        dept.name.toLowerCase().includes(departmentSearchText.toLowerCase())
      );
      setFilteredDepartments(filtered);
    } else {
      setFilteredDepartments(departments);
    }
  }, [departmentSearchText, departments]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setShowDropdown(true);
    setUniversityId(0);
    setUniversityName('');
  };

  const handleSelectUniversity = (uni: University) => {
    setUniversityId(uni.id);
    setUniversityName(uni.name);
    setSearchText(uni.name);
    setShowDropdown(false);
  };

  const clearSearch = () => {
    setSearchText('');
    setUniversityId(0);
    setUniversityName('');
    setShowDropdown(false);
  };

  const handleDepartmentSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDepartmentSearchText(e.target.value);
    setShowDepartmentDropdown(true);
    setDepartmentId(0);
  };

  const handleSelectDepartment = (dept: Department) => {
    setDepartmentId(dept.id);
    setDepartmentSearchText(dept.name);
    setShowDepartmentDropdown(false);
  };

  const clearDepartmentSearch = () => {
    setDepartmentSearchText('');
    setDepartmentId(0);
    setShowDepartmentDropdown(false);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleDotClick = (targetStep: number) => {
    // step 3에 도달한 적이 있으면 자유롭게 이동 가능
    if (hasReachedStep3) {
      setStep(targetStep);
    }
  };

  const handleRequestVerification = async () => {
    if (!email) {
      alert('이메일을 입력해주세요.');
      return;
    }

    setIsRequestingCode(true);
    try {
      await authService.requestVerificationCode({ email });
      alert('인증번호가 발송되었습니다.');
      setRemainingTime(600); // 10분 = 600초
      setIsCodeSent(true);
    } catch (error) {
      console.error('인증번호 요청 실패:', error);
      alert('인증번호 요청에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsRequestingCode(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!universityId || universityId === 0) {
        alert('대학교를 선택해주세요.');
        return;
      }
      if (!email) {
        alert('이메일을 입력해주세요.');
        return;
      }
      if (!verificationCode) {
        alert('인증번호를 입력해주세요.');
        return;
      }
      if (!isCodeSent) {
        alert('인증번호를 먼저 요청해주세요.');
        return;
      }

      setIsVerifying(true);
      try {
        const response = await authService.verifyCode({ email, code: verificationCode });
        setVerificationToken(response.verificationToken);
        alert('인증이 완료되었습니다.');
        setStep(2);
      } catch (error) {
        console.error('인증번호 확인 실패:', error);
        alert('인증번호가 올바르지 않습니다. 다시 확인해주세요.');
      } finally {
        setIsVerifying(false);
      }
    } else if (step === 2) {
      if (!password) {
        alert('비밀번호를 입력해주세요.');
        return;
      }
      if (!passwordConfirm) {
        alert('비밀번호 확인을 입력해주세요.');
        return;
      }
      if (password !== passwordConfirm) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }
      if (!name) {
        alert('이름을 입력해주세요.');
        return;
      }
      if (!studentId) {
        alert('학번을 입력해주세요.');
        return;
      }
      if (!departmentId || departmentId === 0) {
        alert('전공(학과)을 선택해주세요.');
        return;
      }

      setStep(3);
      setHasReachedStep3(true);
    } else if (step === 3) {
      setIsRegistering(true);
      try {
        let profileImageUrl: string | undefined;

        // 프로필 이미지 업로드
        if (profileImage) {
          setIsUploading(true);
          const uploadResult = await memberService.uploadProfileImage(profileImage);
          profileImageUrl = uploadResult.url;
          setIsUploading(false);
        }

        // 회원가입 API 호출
        await memberService.register({
          email,
          password,
          name,
          profileImageUrl,
          studentId,
          universityId,
          departmentId,
          verificationToken
        });

        navigate('/signup-complete');
      } catch (error) {
        console.error('회원가입 실패:', error);
        alert('회원가입에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setIsRegistering(false);
        setIsUploading(false);
      }
    }
  };

  return (
    <S.Page>
      <S.Header>
        <S.Title>
          {step === 1 ? '본인 인증' : step === 2 ? '정보 입력' : '프로필 설정'}
        </S.Title>
      </S.Header>

      <S.ProgressContainer>
        <S.ProgressDot
          active={step === 1}
          onClick={() => handleDotClick(1)}
          clickable={hasReachedStep3}
        />
        <S.ProgressDot
          active={step === 2}
          onClick={() => handleDotClick(2)}
          clickable={hasReachedStep3}
        />
        <S.ProgressDot
          active={step === 3}
          onClick={() => handleDotClick(3)}
          clickable={hasReachedStep3}
        />
      </S.ProgressContainer>

      <S.ContentWrap>
        {step === 1 && (
          <>
            <S.Label>학교</S.Label>
            <S.SearchContainer ref={dropdownRef}>
              <S.InputContainer>
                <S.Input
                  placeholder='대학교를 검색해주세요'
                  value={searchText}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                />
                {searchText && (
                  <S.ClearButton onClick={clearSearch}>
                    <S.ClearIcon>×</S.ClearIcon>
                  </S.ClearButton>
                )}
              </S.InputContainer>
              {showDropdown && filteredUniversities.length > 0 && (
                <S.DropdownList>
                  {filteredUniversities.map((uni) => (
                    <S.DropdownItem
                      key={uni.id}
                      onClick={() => handleSelectUniversity(uni)}
                      selected={uni.id === universityId}
                    >
                      {uni.name}
                    </S.DropdownItem>
                  ))}
                </S.DropdownList>
              )}
              {showDropdown && searchText && filteredUniversities.length === 0 && (
                <S.DropdownList>
                  <S.NoResult>검색 결과가 없습니다</S.NoResult>
                </S.DropdownList>
              )}
            </S.SearchContainer>

            <S.Label>학교이메일</S.Label>
            <S.EmailContainer>
              <S.Input
                placeholder='이메일을 입력해주세요'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ flex: 1 }}
              />
              <S.VerifyButton onClick={handleRequestVerification} disabled={isRequestingCode}>
                {isRequestingCode ? '전송 중...' : isCodeSent ? '재전송' : '인증요청'}
              </S.VerifyButton>
            </S.EmailContainer>

            <S.InputContainer style={{ marginTop: '12px' }}>
              <S.Input
                placeholder='인증번호를 입력해주세요'
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                style={{ paddingRight: isCodeSent ? '80px' : '48px' }}
              />
              {isCodeSent && remainingTime > 0 && (
                <S.Timer>{formatTime(remainingTime)}</S.Timer>
              )}
              {isCodeSent && remainingTime === 0 && (
                <S.TimerExpired>시간만료</S.TimerExpired>
              )}
            </S.InputContainer>
          </>
        )}

        {step === 2 && (
          <>
            <S.Label>이메일</S.Label>
            <S.InputContainer>
              <S.Input
                value={email}
                disabled
                style={{ backgroundColor: '#EBEBEB', color: '#666' }}
              />
            </S.InputContainer>

            <S.Label>비밀번호</S.Label>
            <S.InputContainer>
              <S.Input
                type={showPassword ? "text" : "password"}
                placeholder="비밀번호를 입력해주세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <S.PasswordToggleButton
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                <S.PasswordToggleIcon src={viewIcon} alt="비밀번호 보기" />
              </S.PasswordToggleButton>
            </S.InputContainer>
            <S.InputContainer style={{ marginTop: '8px' }}>
              <S.Input
                type={showPasswordConfirm ? "text" : "password"}
                placeholder="비밀번호를 다시 입력해주세요"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
              <S.PasswordToggleButton
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
              >
                <S.PasswordToggleIcon src={viewIcon} alt="비밀번호 보기" />
              </S.PasswordToggleButton>
            </S.InputContainer>
            {passwordConfirm && password !== passwordConfirm && (
              <S.ErrorText>비밀번호가 일치하지 않습니다.</S.ErrorText>
            )}
            <S.HelperText>
              영문과 숫자를 조합하여 7~15자의 비밀번호를 입력해주세요.<br />
              특수문자(!, @, #, $, % , &, *) 가능
            </S.HelperText>

            <S.Label>이름</S.Label>
            <S.InputContainer>
              <S.Input
                placeholder="이름을 입력해주세요"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </S.InputContainer>

            <S.Label>학번</S.Label>
            <S.InputContainer>
              <S.Input
                placeholder="학번을 입력해주세요"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
              />
            </S.InputContainer>

            <S.Label>전공(학과)</S.Label>
            <S.SearchContainer ref={departmentDropdownRef}>
              <S.InputContainer>
                <S.Input
                  placeholder="전공을 검색해주세요"
                  value={departmentSearchText}
                  onChange={handleDepartmentSearchChange}
                  onFocus={() => setShowDepartmentDropdown(true)}
                />
                {departmentSearchText && (
                  <S.ClearButton onClick={clearDepartmentSearch}>
                    <S.ClearIcon>×</S.ClearIcon>
                  </S.ClearButton>
                )}
              </S.InputContainer>
              {showDepartmentDropdown && filteredDepartments.length > 0 && (
                <S.DropdownList>
                  {filteredDepartments.map((dept) => (
                    <S.DropdownItem
                      key={dept.id}
                      onClick={() => handleSelectDepartment(dept)}
                      selected={dept.id === departmentId}
                    >
                      {dept.name}
                    </S.DropdownItem>
                  ))}
                </S.DropdownList>
              )}
              {showDepartmentDropdown && departmentSearchText && filteredDepartments.length === 0 && (
                <S.DropdownList>
                  <S.NoResult>검색 결과가 없습니다</S.NoResult>
                </S.DropdownList>
              )}
            </S.SearchContainer>
          </>
        )}

        {step === 3 && (
          <>
            <S.ProfileImageSection>
              <S.ProfileImageContainer>
                <S.ProfileImageWrapper onClick={handleImageClick}>
                  <S.ProfileImagePreview
                    src={profileImagePreview || defaultProfileImg}
                    alt="프로필 이미지"
                  />
                </S.ProfileImageWrapper>
                <S.AddPhotoButton onClick={handleImageClick} type="button" />
              </S.ProfileImageContainer>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
            </S.ProfileImageSection>
            <S.HelperText style={{ textAlign: 'center', marginTop: '16px' }}>
              프로필 사진은 선택사항입니다.<br />
              나중에 설정할 수 있습니다.
            </S.HelperText>
          </>
        )}
      </S.ContentWrap>

      <S.BottomButton
        onClick={handleNext}
        disabled={isVerifying || isRegistering || isUploading}
      >
        {isUploading ? '이미지 업로드 중...' : isRegistering ? '가입 중...' : isVerifying ? '확인 중...' : step === 3 ? '가입하기' : '확인'}
      </S.BottomButton>
    </S.Page>
  );
}

export default Signup;
